import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CapTablesService } from 'api';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  AggregatedFundOwnershipDetail,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  UseWaterfallIrrParams,
  UseWaterfallIrrResult,
} from './useWaterfallIrr.props';

export type { UseWaterfallIrrParams, UseWaterfallIrrResult } from './useWaterfallIrr.props';

export const useWaterfallIrr = ({
  workbookCells,
  selectedCaptable,
  exitDate,
  onChange,
  setFundIrrMap,
  isFirmPage,
  companyList,
  isFinancialsLoading,
}: UseWaterfallIrrParams): UseWaterfallIrrResult => {
  const irrCache = useRef<{ [key: string]: Promise<number | null> }>({});

  // Check if inputs are loaded and ready for IRR calculation
  const isInputsLoaded = useMemo(() => {
    if (isFirmPage) {
      return !!companyList?.length && !isFinancialsLoading;
    }
    return !isFinancialsLoading;
  }, [isFirmPage, companyList, isFinancialsLoading]);

  // Fetch IRR data with caching. The isCflOnlyFund flag participates in the cache key so a
  // CFL-only fund's "0 unrealized" call doesn't collide with an unrelated regular fund that
  // happens to have a 0 exit value at the current measurement date.
  const getIrrData = useCallback(
    async (captableId: number, fundId: number, unrealizedValue: number, exit: string, isCflOnlyFund = false) => {
      const cacheKey = `${captableId}-${fundId}-${unrealizedValue}-${exit}-${isCflOnlyFund ? 'cfl' : 'fod'}`;

      if (cacheKey in irrCache.current) {
        return irrCache.current[cacheKey];
      }

      const promise = CapTablesService.apiCapTablesGrossIrrRead(
        captableId,
        fundId,
        unrealizedValue.toString(),
        exit
      )
        .then(response => response.gross_irr)
        .catch(error => {
          delete irrCache.current[cacheKey];
          throw error;
        });

      irrCache.current[cacheKey] = promise;
      return promise;
    },
    []
  );

  // Fetch and update IRR values when inputs are ready
  useEffect(() => {
    if (workbookCells?.waterfallFundSummary && selectedCaptable && exitDate && isInputsLoaded) {
      const waterfallFundSummaryCells = Object.values(
        workbookCells.waterfallFundSummary ?? {}
      ) as EvaluatedCell<AggregatedFundOwnershipDetail>[];

      // Fire IRR when we have an exit value to discount, OR when the row is a CFL-only fund
      // (exit proceeds is 0 because there are no FOD rows, but cfl_invested > 0 means we
      // still need a gross IRR computed off the CFL cash flows alone).
      const equityValueCells = waterfallFundSummaryCells.filter(
        cell => cell.alias === 'exitProceeds' && (cell.value !== 0 || cell.data?.isCflOnlyFund === true)
      );

      if (equityValueCells.every(cell => !cell.invalidExpr)) {
        equityValueCells.forEach(cell => {
          getIrrData(
            selectedCaptable,
            cell.data?.fund as number,
            cell.value as number,
            exitDate,
            cell.data?.isCflOnlyFund === true
          ).then(grossIrr => {
            // Find the irr cell for this row by alias rather than a fixed column letter,
            // because the irr column shifts when the optional loanValue column is present.
            const relatedCell = Object.values(workbookCells.waterfallFundSummary).find(
              c => c.alias === 'irr' && c.rowNumber === cell.rowNumber
            );
            if (relatedCell) {
              const fundId = cell.data?.fund;
              // Store and check by fund ID — matches how WaterfallFundSummaryRowConfig reads
              // fundIrrMap (keyed by detail.fund). Consistent keys prevent an infinite update
              // loop where setFundIrrMap always returns a new object because it was checking
              // one key (fund ID) but writing to a different key (row number).
              if (fundId) {
                setFundIrrMap(prev => {
                  if (prev[fundId] !== grossIrr) {
                    return { ...prev, [fundId]: grossIrr as number ?? 0 };
                  }
                  return prev;
                });
              }
              onChange(relatedCell, grossIrr);
            }
          });
        });
      }
    }
  }, [workbookCells, selectedCaptable, onChange, exitDate, getIrrData, isInputsLoaded, setFundIrrMap]);

  return {
    isInputsLoaded,
  };
};
